<script setup>
const props = defineProps({
  amount: {
    type: Number,
    required: true,
  },
  buttonStyle: {
    type: Object,
    required: false,
    default: () => ({
      buttonstyle: 'white-outline',
      type: 'plain',
      buttonWidth: '300px',
      buttonHeight: '45px',
    }),
  },
  responsive: {
    type: Boolean,
    required: false,
    default: false,
  },
})
const emit = defineEmits(['tokenized'])
const { $config } = useNuxtApp()
const nonce = ref('')
const buttonStyle = ref(props.buttonStyle)
const error = ref('')
const braintreeStore = useBraintree()
const applePayCheckoutInstance = ref(null)
const cssVars = computed(() => {
  return {
    '--button-width': buttonStyle.value.buttonWidth,
    '--button-height': buttonStyle.value.buttonHeight,
  }
})

function adjustApplePayButtonSize() {
  const width = window.innerWidth
  // This matches the responsive style of the PayPal button
  let buttonHeight = '52px'
  if (width < 528) {
    buttonHeight = '42px'
  }

  buttonStyle.value.buttonHeight = buttonHeight
}
function applePayButtonClicked() {
  const checkoutInstance = applePayCheckoutInstance.value
  const merchantName = $config.public.applePayMerchantName
  const paymentRequest = checkoutInstance.createPaymentRequest({
    countryCode: 'US',
    currencyCode: 'USD',
    supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
    total: {
      label: merchantName,
      amount: props.amount,
    },
    requiredBillingContactFields: ['postalAddress', 'email'],
  })

  const session = new window.ApplePaySession(3, paymentRequest)

  session.onvalidatemerchant = function (event) {
    checkoutInstance.performValidation(
      {
        validationURL: event.validationURL,
        displayName: merchantName,
      },
      function (err, merchantSession) {
        if (err) {
          console.error(err)
          error.value =
            'An error occurred while processing the Apple Pay payment.'
        }
        session.completeMerchantValidation(merchantSession)
      },
    )
  }
  session.onpaymentauthorized = function (event) {
    checkoutInstance.tokenize(
      {
        token: event.payment.token,
      },
      function (tokenizeErr, payload) {
        if (tokenizeErr) {
          console.error('Error tokenizing Apple Pay:', tokenizeErr)
          session.completePayment(window.ApplePaySession.STATUS_FAILURE)
          return
        }
        error.value = ''
        nonce.value = payload.nonce

        payload.billingContact = event.payment.billingContact
        payload.paymentMethod = event.payment.token.paymentMethod

        emit('tokenized', payload)

        session.completePayment(window.ApplePaySession.STATUS_SUCCESS)
      },
    )
  }

  session.begin()
}
onMounted(async () => {
  // these need to be imported this way to avoid ssr error
  const braintree = await import('braintree-web')
  braintree.applePay
    .create({ client: await braintreeStore.getClientInstance() })
    .then((instance) => {
      applePayCheckoutInstance.value = instance
    })
  if (props.responsive) {
    window.addEventListener('resize', adjustApplePayButtonSize)
    adjustApplePayButtonSize()
  }
})
onBeforeUnmount(() => {
  if (props.responsive) {
    window.removeEventListener('resize', adjustApplePayButtonSize)
  }
})
</script>

<template>
  <client-only>
    <div class="container">
      <div class="col-6 offset-3">
        <div class="card bg-light">
          <div class="card-body" :style="cssVars">
            <form>
              <span v-if="error" class="error">Error: {{ error }}</span>
              <apple-pay-button
                :buttonstyle="buttonStyle.buttonstyle"
                :type="buttonStyle.type"
                locale="en-US"
                @click="applePayButtonClicked"
              ></apple-pay-button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </client-only>
</template>

<style>
apple-pay-button {
  --apple-pay-button-width: var(--button-width);
  --apple-pay-button-height: var(--button-height);
  --apple-pay-button-border-radius: 3px;
  --apple-pay-button-padding: 0px 0px;
  --apple-pay-button-box-sizing: border-box;
}
</style>
